import { useState, type CSSProperties } from 'react';
import { LEVELS } from '../content/sequence';
import { CHAPTERS, chapterOf } from '../content/chapters';
import { cheapestUnowned } from '../content/hq';
import { isMastered } from '../engine/mastery';
import { chargeFill } from '../engine/fluency';
import { useGameStore } from '../store/gameStore';
import { useCountUp } from './useCountUp';
import { SpeedMeter } from './SpeedMeter';

/** The home hub (the front door). Story Mode is the main event: a big CURRENT-CHAPTER hero
 *  card (villain + one-tap CONTINUE) with the 7-villain arc strip beneath it that opens the
 *  full Story Map. His Base (with the loud BUILD! badge), Powers, and Speed sit below as
 *  smaller cards. Big picture buttons, little reading. */
export function HomeScreen({
  onPlay,
  onMap,
  onBase,
  onPowers,
}: {
  onPlay: () => void;
  onMap: () => void;
  onBase: () => void;
  onPowers: () => void;
}) {
  const coins = useGameStore((s) => s.coins);
  const owned = useGameStore((s) => s.owned);
  const band = useGameStore((s) => s.band);
  const mastery = useGameStore((s) => s.mastery);
  const fluency = useGameStore((s) => s.fluency);
  const currentBeatIds = useGameStore((s) => s.currentBeatIds);
  const beatIndex = useGameStore((s) => s.beatIndex);
  const shownCoins = useCountUp(coins);

  const inProgress = currentBeatIds.length > 0 && beatIndex < currentBeatIds.length;
  const chapter = chapterOf(band);
  const curOrder = chapter?.order ?? 1;

  const next = cheapestUnowned(owned);
  const canBuild = !!next && coins >= next.cost;

  const avgWpm = fluency.recentWpm.length
    ? fluency.recentWpm.reduce((a, b) => a + b, 0) / fluency.recentWpm.length
    : 0;

  return (
    <div style={screen}>
      <div style={header}>
        <span style={title}>🕸️ Crack the Code</span>
        <span style={coinChip}>🪙 {shownCoins}</span>
      </div>

      {/* The main event: the current chapter, one tap to play. */}
      <button type="button" style={hero} onClick={onPlay}>
        <div style={heroArtCol}>
          <HeroVillain id={chapter?.villain.id ?? 'goblin'} emoji={chapter?.villain.emoji ?? '🦹'} />
        </div>
        <div style={heroTextCol}>
          <span style={heroKicker}>CHAPTER {curOrder}</span>
          <span style={heroTitle}>{chapter?.villain.name ?? 'The Villain'}</span>
          <span style={heroSub}>{chapter ? `${chapter.team} · ${chapter.location}` : ''}</span>
          <span style={heroCta}>▶ {inProgress ? 'KEEP READING' : 'PLAY'}</span>
        </div>
      </button>

      {/* The 7-villain arc — tap to open the full Story Map. */}
      <button type="button" style={strip} onClick={onMap} aria-label="Story map">
        <span style={stripLabel}>🗺️ Story</span>
        <span style={stripNodes}>
          {CHAPTERS.map((c) => {
            const state = c.order < curOrder ? 'beaten' : c.order === curOrder ? 'current' : 'locked';
            return (
              <span key={c.id} style={{ ...dot, ...(state === 'current' ? dotNow : null) }}>
                {state === 'beaten' ? '⭐' : state === 'current' ? c.villain.emoji : '🔒'}
              </span>
            );
          })}
        </span>
        <span style={stripGo}>Map ▸</span>
      </button>

      <div style={cardRow}>
        <button type="button" style={{ ...card, ...(canBuild ? cardHot : null) }} onClick={onBase}>
          <div style={cardTop}>
            <span style={cardIcon}>🏛️</span>
            {canBuild && <span style={badge}>BUILD!</span>}
          </div>
          <span style={cardTitle}>My Base</span>
          <span style={cardSub}>
            {next ? (canBuild ? `Build the ${next.name}!` : `${next.cost - coins} more for the ${next.name}`) : 'All built! 🎉'}
          </span>
        </button>

        <button type="button" style={card} onClick={onPowers}>
          <div style={cardTop}>
            <span style={cardIcon}>🦸</span>
          </div>
          <span style={cardTitle}>My Powers</span>
          <span style={powerChips}>
            {LEVELS.filter((l) => !l.stub).map((l) => {
              const mastered = l.patterns.length > 0 && l.patterns.every((p) => isMastered(mastery[p.id]));
              const started = l.patterns.some((p) => mastery[p.id]);
              return (
                <span key={l.id} style={{ ...pchip, ...(mastered ? pchipMastered : started ? pchipStarted : null) }}>
                  {mastered ? '⭐' : '●'}
                </span>
              );
            })}
          </span>
        </button>

        <button type="button" style={card} onClick={onPlay}>
          <div style={cardTop}>
            <span style={cardIcon}>⚡</span>
          </div>
          <span style={cardTitle}>Reading Speed</span>
          {avgWpm > 0 ? (
            <div style={{ marginTop: 4 }}>
              <SpeedMeter fill={chargeFill(avgWpm)} label="" />
            </div>
          ) : (
            <span style={cardSub}>Play a Speed Run to grow it!</span>
          )}
        </button>
      </div>
    </div>
  );
}

/** Current chapter's villain art for the hero card (emoji fallback). */
function HeroVillain({ id, emoji }: { id: string; emoji: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <span style={{ fontSize: 92, lineHeight: 1 }}>{emoji}</span>;
  return (
    <img
      src={`${import.meta.env.BASE_URL}villains/${id}.png`}
      width={104}
      height={104}
      alt=""
      draggable={false}
      onError={() => setFailed(true)}
      style={{ imageRendering: 'pixelated' }}
    />
  );
}

const screen: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'var(--frame)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 14,
  padding: '18px 24px',
  overflow: 'auto',
};
const header: CSSProperties = { width: '100%', maxWidth: 820, display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
const title: CSSProperties = { fontSize: 26, fontWeight: 800, color: 'var(--gold)' };
const coinChip: CSSProperties = { padding: '6px 18px', borderRadius: 999, background: 'var(--frame-2)', color: 'var(--gold)', fontWeight: 800, fontSize: 24 };

const hero: CSSProperties = {
  width: 'min(820px, 100%)',
  display: 'flex',
  alignItems: 'center',
  gap: 20,
  padding: '18px 24px',
  borderRadius: 24,
  border: '3px solid var(--gold)',
  background: 'linear-gradient(135deg, #2a1840, var(--frame-2))',
  color: '#fff',
  cursor: 'pointer',
  textAlign: 'left',
};
const heroArtCol: CSSProperties = { flexShrink: 0, width: 110, height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const heroTextCol: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', gap: 2 };
const heroKicker: CSSProperties = { fontSize: 15, fontWeight: 800, letterSpacing: 2, color: 'var(--gold)' };
const heroTitle: CSSProperties = { fontFamily: 'var(--font-read)', fontSize: 'clamp(28px, 5vmin, 44px)', fontWeight: 800 };
const heroSub: CSSProperties = { fontSize: 15, fontWeight: 700, color: '#cdd6ee', marginBottom: 6 };
const heroCta: CSSProperties = {
  alignSelf: 'flex-start',
  fontSize: 'clamp(22px, 3.6vmin, 30px)',
  fontWeight: 800,
  color: '#fff',
  background: 'var(--spidey-red)',
  borderRadius: 999,
  padding: '8px 26px',
};

const strip: CSSProperties = {
  width: 'min(820px, 100%)',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 18px',
  borderRadius: 16,
  border: '2px solid var(--frame-edge)',
  background: 'var(--frame-2)',
  cursor: 'pointer',
};
const stripLabel: CSSProperties = { fontSize: 16, fontWeight: 800, color: '#f4f4f4', flexShrink: 0 };
const stripNodes: CSSProperties = { flex: 1, display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' };
const dot: CSSProperties = { fontSize: 22, opacity: 0.85 };
const dotNow: CSSProperties = { transform: 'scale(1.35)', opacity: 1 };
const stripGo: CSSProperties = { fontSize: 15, fontWeight: 800, color: 'var(--gold)', flexShrink: 0 };

const cardRow: CSSProperties = { width: 'min(820px, 100%)', display: 'flex', gap: 14, flexWrap: 'wrap' };
const card: CSSProperties = {
  flex: '1 1 200px',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: '14px 16px',
  borderRadius: 18,
  border: '2px solid var(--frame-edge)',
  background: 'var(--frame-2)',
  color: '#f4f4f4',
  textAlign: 'left',
  cursor: 'pointer',
  minHeight: 104,
};
const cardHot: CSSProperties = { borderColor: 'var(--gold)' };
const cardTop: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
const cardIcon: CSSProperties = { fontSize: 30 };
const badge: CSSProperties = {
  fontSize: 14,
  fontWeight: 800,
  color: 'var(--frame)',
  background: 'var(--gold)',
  borderRadius: 999,
  padding: '4px 12px',
  animation: 'ctc-glow 1.3s ease-in-out infinite',
};
const cardTitle: CSSProperties = { fontSize: 18, fontWeight: 800 };
const cardSub: CSSProperties = { fontSize: 13, fontWeight: 700, color: '#cdd6ee' };
const powerChips: CSSProperties = { display: 'flex', gap: 6, marginTop: 4 };
const pchip: CSSProperties = { fontSize: 16, color: '#56607a' };
const pchipStarted: CSSProperties = { color: '#f4f4f4' };
const pchipMastered: CSSProperties = { color: 'var(--gold)' };
